import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import CustomTextField from "../../components/Textfield";
import { Box, useMediaQuery } from "@mui/material";
import Button from "../../components/Button";
import DashboardWrapper from "../../components/DashboardWrapper";
import UserDetail from "./UserDetail";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { useTheme } from "@emotion/react";
import TableComponent from "../../components/Table";
import { getUsers } from "../../http/auth";
import Spinner from "../../components/Spinner";
import CustomSelectField from "../../components/Select";
import InviteUser from "./InviteUser";
import CreateUser from "./CreateUser";
import useDebounce from "./useDebounce";

const columns = [
  { id: "sr", label: "Sl. No.", minWidth: 30 },
  { id: "name", label: "Name", minWidth: 80, sortable: true },
  { id: "email", label: "Email", minWidth: 170 },
  { id: "phone", label: "Phone Number", minWidth: 80 },
  { id: "status", label: "Status", minWidth: 40 },
  { id: "view", label: "Action", minWidth: 40 },
];

function createData(sr, name, email, phone, status, view, id) {
  return { sr, name, email, phone, status, view, id };
}

export default function ManageUsers() {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [searchString, setSearchString] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(["User"]);
  const [create, setCreate] = useState("");
  const [invite, setInvite] = useState("");
  const [nextId, setNextId] = useState("");

  const params = new URLSearchParams(document.location.search);
  const id = params.get("id");
  const debouncedSearchString = useDebounce(searchString, 300);

  useEffect(() => {
    getUserList();
  }, [debouncedSearchString]);

  useEffect(() => {
    if (sortOrder) {
      setLoading(true);
      const sortedRows = rows.sort((a, b) => {
        if (sortOrder === "asc") return a.name.localeCompare(b.name);
        else if (sortOrder === "desc") return b.name.localeCompare(a.name);
        else return a.sr - b.sr;
      });
      setTimeout(() => {
        setRows(sortedRows);
        setLoading(false);
      }, 300);
    }
  }, [sortOrder]);

  const getUserList = async (val, id, userTypeChange) => {
    try {
      setLoading(true);
      const user = val?.[0] || userType[0];
      const response = await getUsers(user.toLowerCase(), searchString, id);
      const users = response.data?.data?.users?.data;
      setNextId(response.data?.data?.users?.nextId);
      if (users) updateRows(users, userTypeChange);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const updateRows = (users, userTypeChange) => {
    const currentCount = nextId ? rows.length : 0;
    const list = users.map((item, index) => {
      return createData(
        currentCount + index + 1,
        item.name || "-",
        item.email || "-",
        item.phone || "-",
        item.isAccountBlocked ? "block" : "unblock",
        "",
        item._id
      );
    });
    const updatedList = nextId && !userTypeChange ? [...rows, ...list] : list;
    setRows(updatedList);
  };
  
  const dataUpdateStatusChange = async (id, status) => {
    const newList = [...rows];
    const userIndex = newList.findIndex((item) => item.id === id);
    newList[userIndex].status = status ? "block" : "unblock";
    setRows(newList);
  }

  const onNextClick = async () => {
    setPage(page + 1);
    if (nextId && page === rows.length / 10 - 1) {
      try {
        await getUserList(null, nextId);
      } catch (error) {
        console.log(error, "error");
      }
    }
  };

  const onPrevClick = async () => {
    setPage(page - 1);
  };

  const onChange = (event) => {
    setSearchString(event.target.value);
  };

  const onChangeUserType = async (val) => {
    await getUserList(val, null, true);
    setPage(0);
    setUserType(val);
  };

  return (
    <DashboardWrapper userId={id}>
      <CreateUser open={create} setOpen={setCreate} />
      <InviteUser open={invite} setOpen={setInvite} />
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
          boxShadow: "none",
        }}
      >
        {id ? (
          <UserDetail />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                // justifyContent: "space-between",
                alignItems: "center",
                gap: "2rem",
              }}
            >
              <Box sx={{ width: "130px" }}>
                <CustomSelectField
                  type="text"
                  variant="outlined"
                  placeholder="User Type "
                  value={userType}
                  errorMessage={"errorMessage"}
                  edit={false}
                  border
                  onChange={onChangeUserType}
                />
              </Box>
              <CustomTextField
                sx={{
                  width: "50%",
                  minWidth: smScreen ? "" : "300px",
                  maxWidth: "400px",
                  "& .MuiInputBase-input": { padding: "9px 16px 9px 48px" },
                }}
                name="search"
                placeholder="Search"
                value={searchString || ""}
                onChange={onChange}
                type="search"
              />
              <Button
                variant="contained"
                label="Create User"
                style={{ width: "150px", marginLeft: "auto" }}
                onClick={() => setCreate(true)}
              />
              <Button
                variant="contained"
                label="Invite"
                style={{ width: "150px" }}
                onClick={() => setInvite(true)}
              >
                <PersonAddOutlinedIcon sx={{ marginRight: "8px" }} />
              </Button>
            </Box>
            {!loading ? (
              <TableComponent
                sort
                smScreen={smScreen}
                sortOrder={sortOrder}
                columns={columns}
                rows={rows}
                page={page}
                nextId={nextId}
                rowsPerPage={10}
                getUserList={getUserList}
                setLoading={setLoading}
                setSortOrder={setSortOrder}
                onPrevClick={onPrevClick}
                onNextClick={onNextClick}
                dataUpdateStatusChange={dataUpdateStatusChange}
              />
            ) : (
              <Spinner />
            )}
          </>
        )}
      </Paper>
    </DashboardWrapper>
  );
}
